import { supabase } from '@/integrations/supabase/client';

export interface SnapshotDay {
  snapshot_date: string; // yyyy-mm-dd date
}

export interface ProductSnapRow {
  snapshot_date: string;
  item_code: string | null;
  category: string | null;
  name: string;
  quantity_on_hand: number | null;
  on_hold_quantity: number | null;
  price: number | null;
  initial_cost: number | null;
  current_cost: number | null;
  location_id: string | null;
}

export async function listSnapshotDays(companyId: string): Promise<SnapshotDay[]> {
  // Get distinct snapshot dates from product_inventory_snapshots table, sorted by date descending
  // Use pagination to fetch all snapshots (all-time, not limited by month)
  const pageSize = 1000;
  let allData: any[] = [];
  let page = 0;
  let hasMore = true;

  while (hasMore) {
    const { data, error } = await supabase
      .from('product_inventory_snapshots')
      .select('snapshot_date')
      .eq('company_id', companyId)
      .order('snapshot_date', { ascending: false })
      .range(page * pageSize, (page + 1) * pageSize - 1);
    
    if (error) throw error;
    
    if (data && data.length > 0) {
      allData = [...allData, ...data];
      page++;
      hasMore = data.length === pageSize;
    } else {
      hasMore = false;
    }
  }
  
  // Get unique dates from all fetched data
  const uniqueDates = new Set<string>();
  allData.forEach(row => {
    if (row.snapshot_date) {
      // Extract date part if it's a timestamp
      const dateStr = typeof row.snapshot_date === 'string' 
        ? row.snapshot_date.split('T')[0] 
        : row.snapshot_date;
      uniqueDates.add(dateStr);
    }
  });
  
  // Convert to array and sort descending
  return Array.from(uniqueDates)
    .sort((a, b) => b.localeCompare(a))
    .map(date => ({ snapshot_date: date }));
}

export async function fetchSnapshotRows(companyId: string, snapshotDate: string): Promise<ProductSnapRow[]> {
  // Query product_inventory_snapshots joined with products table
  // Include all rows regardless of quantity_on_hand or on_hold_quantity values
  const { data, error } = await supabase
    .from('product_inventory_snapshots')
    .select(`
      snapshot_date,
      quantity_on_hand,
      on_hold_quantity,
      price,
      initial_cost,
      current_cost,
      products(
        item_code,
        category,
        name,
        location_id
      )
    `)
    .eq('company_id', companyId)
    .eq('snapshot_date', snapshotDate);
  
  if (error) throw error;
  
  // Transform the nested structure to match the SQL query result
  // Include all rows, even if on_hold_quantity > 0 or quantity_on_hand = 0
  const rows = (data ?? []).map((row: any) => ({
    snapshot_date: typeof row.snapshot_date === 'string' 
      ? row.snapshot_date.split('T')[0] 
      : row.snapshot_date,
    item_code: row.products?.item_code ?? null,
    category: row.products?.category ?? null,
    name: row.products?.name ?? '',
    quantity_on_hand: row.quantity_on_hand ?? null,
    on_hold_quantity: row.on_hold_quantity ?? null,
    price: row.price ?? null,
    initial_cost: row.initial_cost ?? null,
    current_cost: row.current_cost ?? null,
    location_id: row.products?.location_id ?? null,
  }));
  
  // Sort by item_code as specified in the SQL query (order by p.item_code)
  return rows.sort((a, b) => {
    const codeA = a.item_code ?? '';
    const codeB = b.item_code ?? '';
    return codeA.localeCompare(codeB);
  });
}
